'use client';

import { PropsWithChildren, useState } from 'react';
import { buildRoute } from '@headless-workspace/config';
import { PropsWithLocale } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import {
    DataLayerEvent,
    DataLayerEventCategory,
    useDataLayer,
    useNavigation,
    useRecentSearches,
} from '../../../../../../../src';
import {
    SearchActionContext,
    searchActionContextType,
    SearchStateContext,
    searchStateContextType,
} from './SearchContext';

const DATA_LAYER_SEARCH_ACTION = 'page search - click';

type SearchProviderProps = PropsWithLocale & PropsWithChildren;

export const SearchProvider: Component<SearchProviderProps> = ({ locale, children }) => {
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchInput, setSearchInput] = useState('');

    const { addRecentSearch } = useRecentSearches();
    const { trackEvent } = useDataLayer();
    const navigation = useNavigation();

    const onSearch = (search: string) => {
        addRecentSearch(search);
        trackEvent(DataLayerEvent.Click, {
            id: 'click',
            category: DataLayerEventCategory.Search,
            action: DATA_LAYER_SEARCH_ACTION,
            label: encodeURIComponent(search),
        });
        navigation.push(`${buildRoute(locale, 'search')}${encodeURIComponent(search)}`);
    };

    const handleOpen = () => {
        setIsSearchOpen(true);
        // Add 'overflow-hidden' class to prevent scrolling on the body beneath search
        document.body.classList.add('overflow-hidden');
    };

    const handleClose = () => {
        setIsSearchOpen(false);
        // Remove 'overflow-hidden' class to allow scrolling on the body beneath search
        document.body.classList.remove('overflow-hidden');
    };

    const onClearSearches = () => {
        setSearchInput('');
    };

    const onSearchInputChange = (input: string) => {
        setSearchInput(input);
    };

    const searchStateProviderValue: searchStateContextType = {
        isSearchOpen,
        searchInput,
    };

    const searchActionProviderValue: searchActionContextType = {
        onSearch,
        handleOpen,
        handleClose,
        onClearSearches,
        onSearchInputChange,
    };

    return (
        <SearchStateContext.Provider value={searchStateProviderValue}>
            <SearchActionContext.Provider value={searchActionProviderValue}>{children}</SearchActionContext.Provider>
        </SearchStateContext.Provider>
    );
};
